import Service from '@ember/service';
import type { jsPDF, jsPDFOptions } from 'jspdf';
import type { Options } from 'html-to-image/src/types';

const JSPDF_OPTIONS: jsPDFOptions = {
  orientation: 'portrait',
  unit: 'pt',
  format: 'a4',
  compress: true,
};

/**
 * Encapsulates the jsPDF/html-to-image library.
 */
export default class PdfService extends Service {
  doc?: jsPDF;
  jsPDF?: new (options: jsPDFOptions) => jsPDF;
  toCanvas?: (
    node: HTMLElement,
    options: Options
  ) => Promise<HTMLCanvasElement>;
  async start() {
    const { jsPDF } = await import('jspdf');
    const { toCanvas } = await import('html-to-image');
    this.jsPDF = jsPDF;
    this.toCanvas = toCanvas;
    this.resetDoc();
  }

  resetDoc = () => {
    if (this.jsPDF) {
      this.doc = new this.jsPDF(JSPDF_OPTIONS);
    }
  };

  get height() {
    return this.doc ? this.doc.internal.pageSize.getHeight() : 0;
  }

  get width() {
    return this.doc ? this.doc.internal.pageSize.getWidth() : 0;
  }

  async addHtml(element: HTMLElement) {
    if (!this.toCanvas) {
      throw new Error('PdfService not started. Call start() first.');
    }
    const canvas = await this.toCanvas(element, {
      cacheBust: true,
      width: document.documentElement.offsetWidth,
      height: document.documentElement.offsetHeight,
      style: {
        display: 'unset',
        width: '595pt', // width of A4 format
        height: '100%',
      },
    });

    // get proper scale for image
    const canvasWidth = pxToPt(canvas.width);
    const canvasHeight = pxToPt(canvas.height);
    const widthRatio = this?.width / canvasWidth;
    const heightRatio = this?.height / canvasHeight;
    const ratio = widthRatio > heightRatio ? heightRatio : widthRatio;

    if (!this.doc) {
      throw new Error('PdfService not started. Call start() first.');
    }
    this.doc.context2d.autoPaging = true;
    // adds margin to each page to account for header
    this.doc.context2d.margin = [60, 0, 0, 0];
    // draw image across pdf spanning multiple pages if necessary
    this.doc.context2d.drawImage(
      canvas.toDataURL(),
      0,
      0,
      canvasWidth * ratio,
      canvasHeight * ratio
    );
  }

  iterateThroughPages(
    callback: (currentPage: number, totalPages: number) => void
  ) {
    const totalPages = this.doc?.getNumberOfPages();
    if (!totalPages) {
      return;
    }
    for (let currentPage = 1; currentPage <= totalPages; currentPage++) {
      this.doc?.setPage(currentPage);
      callback(currentPage, totalPages);
    }
  }
}

export function pxToPt(px: number) {
  return px * 0.75;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    pdf: PdfService;
  }
}
